import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import {
  ArrowRight, BadgeCheck, Bell, Camera, Check, CheckCircle2, ClipboardList, Copy, Download, Eye,
  Hotel, ImagePlus, LayoutGrid, Link2, MessageCircle, Palette, PenLine, Printer, QrCode, Receipt,
  Scissors, Share2, ShieldCheck, ShoppingBag, Smartphone, Sparkles, Star, Stethoscope, Store,
  Truck, Utensils, Dumbbell, Wrench, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import DemoFormDialog from "@/components/DemoFormDialog";
import FaqAccordion from "@/components/FaqAccordion";
import FinalCta from "@/components/FinalCta";
import { ScaledPoster, KIT_THEMES, WhatsAppGlyph } from "@/components/QrKitPoster";
import { PRODUCT_LIST } from "@/data/products";
import { BRAND } from "@/data/site";
import { useSeo, FaqSchema } from "@/lib/seo";

const SLUG = "qr-kits";

/* ----------------------------------------------------------------------------
 * Helpers (same rules as the app: digits only, bare 10-digit = India +91)
 * ------------------------------------------------------------------------- */
function toWaDigits(raw) {
  let d = String(raw || "").replace(/\D/g, "");
  if (d.length === 10) d = `91${d}`;
  return /^\d{7,15}$/.test(d) ? d : null;
}
function prettyPhone(digits) {
  if (!digits) return "";
  if (digits.startsWith("91") && digits.length === 12) return `+91 ${digits.slice(2, 7)} ${digits.slice(7)}`;
  return `+${digits}`;
}
function waLink(digits, message) {
  const base = `https://wa.me/${digits}`;
  const m = (message || "").trim();
  return m ? `${base}?text=${encodeURIComponent(m)}` : base;
}
// Sample QR for the Rate Us previews: a vendor's real short link only exists
// once their Google Business Profile is connected, so the sample scans to the
// "how it works" section of this page instead of a dead link.
const SAMPLE_RATING_URL =
  typeof window !== "undefined" ? `${window.location.origin}/products/qr-kits#rating-flow` : "https://grabnchat.com";
const SAMPLE_RATING_PILL = "Your short rating link";

/* ----------------------------------------------------------------------------
 * Content
 * ------------------------------------------------------------------------- */
const KITS = [
  {
    key: "whatsapp",
    icon: MessageCircle,
    accent: "#25D366",
    name: "WhatsApp Chat Kit",
    tagline: "Scan to chat",
    desc: "A QR code that opens a WhatsApp chat with your business number — with your welcome message already typed in. Walk-in customers become contacts you can follow up with, send offers to and support later.",
    points: [
      "Opens wa.me/<your number> in the customer's WhatsApp",
      "Optional pre-filled message (up to 120 characters)",
      "Your number is picked up automatically from your connected WhatsApp channel",
      "Customisable headline (up to 40 characters)",
    ],
    steps: [["Open camera", Camera], ["Scan QR code", QrCode], ["Start chatting", MessageCircle]],
  },
  {
    key: "rating",
    icon: Star,
    accent: "#F59E0B",
    name: "Rate Us Kit",
    tagline: "Scan to rate",
    desc: "A QR code that opens your personal short rating link. Customers tap 1–5 stars and leave their name and phone number in about 10 seconds — every rating lands in your GrabNChat app and portal.",
    points: [
      "Short, permanent link for your business (e.g. …/r/aB3xK9p)",
      "Captures star rating, name, phone and an optional written review",
      "Happy customers (4–5★) are sent on to your Google review page",
      "Needs your Google Business Profile connected in GrabNChat",
    ],
    steps: [["Scan QR code", QrCode], ["Tap the stars", Star], ["Share feedback", MessageCircle]],
  },
];

const PLACEMENTS = [
  { icon: Receipt, title: "Billing counter", desc: "Customers scan while they pay — the moment they're most likely to rate you." },
  { icon: Utensils, title: "Table tents", desc: "One per table in restaurants and cafés for menus, orders and reviews." },
  { icon: Store, title: "Shop door & window", desc: "Let passers-by message you even when you're closed." },
  { icon: ClipboardList, title: "Bills & receipts", desc: "Print the QR on invoices so every sale can turn into a chat." },
  { icon: Truck, title: "Delivery packaging", desc: "Stick it on parcels and takeaway bags to win repeat orders." },
  { icon: ShoppingBag, title: "Stalls, fairs & events", desc: "Collect leads at exhibitions without a sign-up sheet." },
];

const INDUSTRIES = [
  { icon: Utensils, title: "Restaurants & cafés" },
  { icon: Scissors, title: "Salons & spas" },
  { icon: Stethoscope, title: "Clinics & diagnostics" },
  { icon: Store, title: "Retail & kirana stores" },
  { icon: Dumbbell, title: "Gyms & studios" },
  { icon: Hotel, title: "Hotels & homestays" },
  { icon: Wrench, title: "Service centres & repair" },
  { icon: ShoppingBag, title: "Showrooms & boutiques" },
];

const FEATURES = [
  { icon: Palette, title: "4 ready-made styles", desc: "Brand, WhatsApp, Navy and Dark colour styles — switch with one tap and the preview updates instantly." },
  { icon: PenLine, title: "Editable headline & message", desc: "Write your own call-to-action, like “Order on WhatsApp” or “Loved your meal? Rate us!”." },
  { icon: ImagePlus, title: "Your logo & business name", desc: "Every poster carries your logo and business name in the header, so it looks like your own print material." },
  { icon: Printer, title: "Print-ready export", desc: "Download a sharp 1440 × 2036 px PNG in A-series (A4/A5/A6) proportions — ready for any print shop." },
  { icon: Share2, title: "Save or share instantly", desc: "Save the poster to your phone's gallery or share it straight to your printer, staff or social media." },
  { icon: Link2, title: "Copyable short link", desc: "Use the same rating link in SMS, email signatures, Instagram bios and invoices — not just on paper." },
  { icon: Bell, title: "Instant rating alerts", desc: "Get a push notification on your phone the moment a customer submits a rating." },
  { icon: LayoutGrid, title: "Ratings dashboard", desc: "See every rating with the customer's name and phone in the GrabNChat app and company portal." },
  { icon: ShieldCheck, title: "Google-policy friendly", desc: "The public Google review option is never hidden from any customer — no review gating, no risk to your profile." },
];

const SETUP = [
  { icon: Smartphone, title: "Connect your channels", desc: "Link your WhatsApp Business number in GrabNChat. For the Rate Us kit, also connect your Google Business Profile and pick your location." },
  { icon: Palette, title: "Design your poster", desc: "Open Profile → QR Kits in the GrabNChat app, choose a kit and style, and edit the headline and pre-filled message." },
  { icon: Download, title: "Download & print", desc: "Save the print-ready PNG or share it. Print it at any shop and place it where customers wait, pay or leave." },
];

const PRINT_SIZES = [
  { size: "A4", mm: "210 × 297 mm", dpi: "≈ 175 dpi", use: "Wall posters, shop doors & windows" },
  { size: "A5", mm: "148 × 210 mm", dpi: "≈ 245 dpi", use: "Counters, reception desks, table stands" },
  { size: "A6", mm: "105 × 148 mm", dpi: "≈ 350 dpi", use: "Table tents, packaging inserts, flyers" },
];

const FAQS = [
  { q: "What is a GrabNChat QR Kit?", a: "QR Kits are ready-to-print posters with a QR code for your business. The WhatsApp Chat kit opens a chat with your WhatsApp number, and the Rate Us kit opens your rating page so customers can rate you in seconds. You create them in the GrabNChat app under Profile → QR Kits." },
  { q: "Do my customers need to install any app?", a: "No. Customers just point their phone camera (or Google Lens) at the QR code. The WhatsApp kit opens WhatsApp directly, and the Rate Us kit opens a lightweight web page that works on any phone." },
  { q: "What do I need before I can create the kits?", a: "For the WhatsApp Chat kit you need a WhatsApp number connected in GrabNChat. For the Rate Us kit you also need your Google Business Profile connected with a business location selected — GrabNChat then creates your short rating link automatically." },
  { q: "What happens when a customer gives a low rating?", a: "Every rating (1–5★) is saved with the customer's name and phone and shows up in your GrabNChat app and portal, with a push notification to you. 4–5★ customers are taken on to your Google review page with their review text copied for easy pasting. 1–3★ customers see a thank-you and their feedback comes to you privately, so you can call them back and fix the issue." },
  { q: "Is this allowed under Google's review policy?", a: "Yes. Google forbids “review gating” — hiding the public review option from unhappy customers. GrabNChat never does that: the link to post a public Google review stays available for every rating. We only change which next step is suggested first." },
  { q: "Will the QR code stop working if I change the poster?", a: "No. Your rating link is created once and stays the same, and the WhatsApp QR always points to your number. You can change the headline, style or message and reprint any time; old posters keep working. The WhatsApp QR only needs reprinting if you change your WhatsApp number." },
  { q: "What size should I print it?", a: "The poster is exported at 1440 × 2036 px in A-series proportions, so it prints cleanly at A4, A5 or A6. A5 is a good default for counters; A6 works well as table tents. Keep the QR code at least 2.5 cm wide and print on matte paper to avoid glare." },
  { q: "Can I use the rating link without printing?", a: "Yes. Copy the short link from the app and send it on WhatsApp after a service, add it to invoices and SMS, or put it in your Instagram bio and email signature." },
  { q: "Can I put my own logo on the poster?", a: "Yes. The poster header shows your business logo and name from your GrabNChat profile. If no logo is set, it shows your business initials instead." },
];

/* ----------------------------------------------------------------------------
 * Page
 * ------------------------------------------------------------------------- */
export default function QrKitPage() {
  useSeo({
    title: "QR Kits – WhatsApp Chat & Google Review Posters | GrabNChat",
    description:
      "Print-ready QR posters for shops, restaurants, salons and clinics. Customers scan to chat with you on WhatsApp or rate you in 10 seconds — ratings and leads land in your GrabNChat app.",
  });

  const otherProducts = PRODUCT_LIST.filter((x) => x.slug !== SLUG).slice(0, 4);
  const salesDigits = toWaDigits(BRAND.whatsapp);

  return (
    <div data-testid="product-page-qr-kits">
      {/* ------------------------------------------------------------ Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-orange-50/60 via-slate-50 to-white">
        <div className="absolute inset-0 bg-dots opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 md:pt-24 pb-16 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-slate-200 bg-white text-xs font-medium text-slate-700 mb-6">
              <QrCode className="w-3.5 h-3.5 text-[#25D366]" /> QR Kits for vendors
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900">
              Turn every walk-in into a <span className="text-[#25D366]">WhatsApp chat</span> and a{" "}
              <span className="text-[#F59E0B]">5-star review</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
              Print-ready QR posters for your counter, tables, door and bills. Customers scan with their phone camera to chat
              with you on WhatsApp or rate you in 10 seconds — and every lead and rating lands in your GrabNChat app.
            </p>
            <ul className="mt-6 grid sm:grid-cols-2 gap-2 max-w-xl">
              {["No app needed for customers", "Ready in 2 minutes", "Print at any shop", "Google-policy friendly"].map((b) => (
                <li key={b} className="flex items-center gap-2 text-sm text-slate-700">
                  <CheckCircle2 className="w-4 h-4 text-[#25D366] shrink-0" /> {b}
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <DemoFormDialog source={SLUG} defaultMessage="I'd like to set up QR Kits for my business." trigger={
                <Button className="bg-[#25D366] hover:bg-[#1DA851] text-white rounded-full px-7 h-12 font-semibold" data-testid="qrkit-hero-demo-btn">
                  Book Free Demo <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              } />
              <a href="#try-it">
                <Button variant="outline" className="w-full bg-white border-slate-200 hover:border-slate-300 text-slate-900 rounded-full px-7 h-12 font-semibold" data-testid="qrkit-hero-try-btn">
                  Build your QR poster
                </Button>
              </a>
            </div>
          </div>

          <div className="relative flex justify-center lg:justify-end">
            <div className="absolute -inset-6 bg-[#25D366]/15 blur-3xl rounded-3xl" aria-hidden />
            <div className="relative flex items-end gap-4 sm:gap-6">
              <div className="w-[46%] max-w-[300px] -rotate-3 translate-y-4">
                <ScaledPoster kit="whatsapp" theme={KIT_THEMES[1]} businessName="Sharma Sweets & Snacks"
                  qrValue={waLink(salesDigits, "Hi! I saw the GrabNChat QR Kit and want one for my shop.")}
                  pillText={prettyPhone(salesDigits)} />
              </div>
              <div className="w-[46%] max-w-[300px] rotate-3">
                <ScaledPoster kit="rating" theme={KIT_THEMES[0]} businessName="Glow Unisex Salon" headline="Loved your visit?"
                  qrValue={SAMPLE_RATING_URL} pillText={SAMPLE_RATING_PILL} />
              </div>
            </div>
          </div>
        </div>
        <p className="relative text-center text-xs text-slate-500 pb-8 px-4">
          Tip: scan the green poster with your phone to chat with the GrabNChat team on WhatsApp.
        </p>
      </section>

      {/* ------------------------------------------------------ Kit facts */}
      <section className="border-y border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            ["2", "QR kits — Chat & Rate Us"],
            ["4", "colour styles to match your brand"],
            ["1440 × 2036", "px print-ready PNG"],
            ["A4 · A5 · A6", "print sizes supported"],
          ].map(([v, l]) => (
            <div key={l}>
              <div className="font-display text-2xl sm:text-3xl font-bold text-slate-900">{v}</div>
              <div className="mt-1 text-sm text-slate-600">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ----------------------------------------------------- Two kits */}
      <section className="py-20 md:py-24" id="kits">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Two kits, one app" title="Everything a counter needs to grow your business"
            sub="Put both kits side by side: one grows your WhatsApp contact list, the other grows your Google rating." />
          <div className="mt-12 grid lg:grid-cols-2 gap-6">
            {KITS.map((k) => (
              <div key={k.key} className="min-w-0 bg-white border border-slate-200 rounded-3xl p-6 sm:p-7 md:p-8 hover:shadow-lg transition-shadow" data-testid={`qrkit-kit-${k.key}`}>
                <div className="flex items-center gap-3">
                  <span className="w-12 h-12 rounded-2xl grid place-items-center text-white" style={{ background: k.accent }}>
                    {k.key === "whatsapp" ? <WhatsAppGlyph className="w-6 h-6" /> : <k.icon className="w-6 h-6" fill="currentColor" />}
                  </span>
                  <div>
                    <div className="text-xs uppercase tracking-widest font-semibold" style={{ color: k.accent }}>{k.tagline}</div>
                    <h3 className="font-display text-2xl font-bold text-slate-900">{k.name}</h3>
                  </div>
                </div>
                <p className="mt-5 text-slate-600 leading-relaxed">{k.desc}</p>
                <ul className="mt-5 space-y-2.5">
                  {k.points.map((pt) => (
                    <li key={pt} className="flex items-start gap-2.5 text-sm text-slate-800">
                      <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: k.accent }} /> {pt}
                    </li>
                  ))}
                </ul>
                <div className="mt-6 pt-6 border-t border-slate-100 flex items-start justify-between gap-1 sm:gap-2">
                  {k.steps.map(([label, Icon], i) => (
                    <div key={label} className="contents">
                      {i > 0 && <ArrowRight className="w-4 h-4 mt-2 text-slate-300 shrink-0" />}
                      <div className="flex-1 min-w-0 flex flex-col sm:flex-row items-center gap-1.5 sm:gap-2 text-center sm:text-left">
                        <span className="w-8 h-8 rounded-full grid place-items-center shrink-0" style={{ background: `${k.accent}1A`, color: k.accent }}>
                          <Icon className="w-4 h-4" />
                        </span>
                        <span className="text-xs sm:text-sm font-medium text-slate-700 leading-tight">{label}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------ Live builder */}
      <PosterBuilder salesDigits={salesDigits} />

      {/* -------------------------------------------- Rating flow */}
      <section className="py-20 md:py-24" id="rating-flow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="How the Rate Us kit works" title="More Google reviews, and unhappy customers come to you first"
            sub="Every scan becomes a saved rating with a name and phone number. What happens next depends on the stars." />
          <div className="mt-12 grid lg:grid-cols-3 gap-6 items-stretch">
            <FlowCard step="1" icon={QrCode} color="#0F172A" title="Customer scans & rates"
              points={["Scans the QR with their phone camera", "Taps 1 to 5 stars", "Enters name & phone, plus an optional review", "Rating is saved to your app & portal instantly, and you get a push alert"]} />
            <FlowCard step="2a" icon={Star} color="#25D366" title="4–5★ → Google review"
              points={["Thank-you screen", "Their review text is copied for easy pasting", "Automatically redirected to your Google review page", "More public 5-star reviews on Google Maps"]} />
            <FlowCard step="2b" icon={MessageCircle} color="#F97316" title="1–3★ → private feedback"
              points={["Thank-you screen; feedback comes to you privately", "You get their name & phone to call back and fix it", "The option to post on Google stays visible, as Google requires", "Turn a bad experience into a loyal customer"]} />
          </div>
          <div className="mt-8 flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-sm text-emerald-900">
            <ShieldCheck className="w-5 h-5 shrink-0 mt-0.5" />
            <p>
              <strong>Built to follow Google's review policy.</strong> Google forbids “review gating” (hiding the public review
              option from unhappy customers), which can get reviews removed or a profile suspended. GrabNChat never hides it: we
              only suggest the best next step for each rating.
            </p>
          </div>
        </div>
      </section>

      {/* ---------------------------------------------- Features */}
      <section className="py-20 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Features" title="Everything included in QR Kits" />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f, i) => (
              <div key={f.title} className="bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-md transition-shadow" data-testid={`qrkit-feature-${i}`}>
                <span className="w-10 h-10 rounded-xl grid place-items-center bg-[#25D366]/10 text-[#25D366]">
                  <f.icon className="w-5 h-5" />
                </span>
                <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">{f.title}</h3>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------------------------------------- Setup steps */}
      <section className="py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Get started" title="Live at your counter in 3 steps" />
          <ol className="mt-12 grid md:grid-cols-3 gap-6">
            {SETUP.map((s, i) => (
              <li key={s.title} className="relative bg-white border border-slate-200 rounded-2xl p-6">
                <div className="flex items-center gap-3">
                  <span className="w-9 h-9 rounded-full bg-[#0F172A] text-white grid place-items-center text-sm font-bold">{i + 1}</span>
                  <s.icon className="w-5 h-5 text-[#25D366]" />
                </div>
                <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">{s.title}</h3>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* -------------------------------------- Placements + industries */}
      <section className="py-20 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Where to use it" title="Put it wherever customers wait, pay or leave" />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {PLACEMENTS.map((p) => (
              <div key={p.title} className="flex gap-4 bg-white border border-slate-200 rounded-2xl p-5">
                <span className="w-10 h-10 shrink-0 rounded-xl grid place-items-center bg-orange-50 text-[#F97316]">
                  <p.icon className="w-5 h-5" />
                </span>
                <div>
                  <h3 className="font-semibold text-slate-900">{p.title}</h3>
                  <p className="mt-1 text-sm text-slate-600 leading-relaxed">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <h3 className="mt-16 font-display text-2xl font-bold text-slate-900">Made for local vendors</h3>
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
            {INDUSTRIES.map((ind) => (
              <div key={ind.title} className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl p-4">
                <ind.icon className="w-5 h-5 text-[#25D366] shrink-0" />
                <span className="text-sm font-medium text-slate-800">{ind.title}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------------------------------------- Print guide */}
      <section className="py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
          <div className="min-w-0 lg:col-span-5">
            <SectionHeading eyebrow="Printing guide" title="Prints sharp at every common size"
              sub="Posters are exported at 1440 × 2036 px in A-series proportions, so they fit A4, A5 and A6 paper without cropping." />
            <ul className="mt-8 space-y-3 text-sm text-slate-700">
              {[
                "Keep the QR code at least 2.5 cm wide; bigger for walls read from a distance.",
                "Use matte or laminated-matte paper so shop lights don't cause glare.",
                "Place it at eye or hand level, close to where people pay or wait.",
                "Scan-test one printout with 2–3 different phones before printing many.",
              ].map((tip) => (
                <li key={tip} className="flex items-start gap-2.5"><CheckCircle2 className="w-4 h-4 text-[#25D366] mt-0.5 shrink-0" /> {tip}</li>
              ))}
            </ul>
          </div>
          <div className="min-w-0 lg:col-span-7">
            <div className="overflow-x-auto rounded-2xl border border-slate-200">
              <table className="w-full text-sm text-left">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-5 py-3 font-semibold">Paper</th>
                    <th className="px-5 py-3 font-semibold">Size</th>
                    <th className="px-5 py-3 font-semibold">Print quality</th>
                    <th className="px-5 py-3 font-semibold">Best for</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {PRINT_SIZES.map((r) => (
                    <tr key={r.size} className="bg-white">
                      <td className="px-5 py-4 font-display font-bold text-slate-900">{r.size}</td>
                      <td className="px-5 py-4 text-slate-700 whitespace-nowrap">{r.mm}</td>
                      <td className="px-5 py-4 text-slate-700 whitespace-nowrap">{r.dpi}</td>
                      <td className="px-5 py-4 text-slate-600">{r.use}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------------------------------------- FAQs */}
      <section className="py-20 md:py-24 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 text-center">QR Kits FAQs</h2>
          <div className="mt-10"><FaqAccordion faqs={FAQS} /></div>
          <FaqSchema faqs={FAQS} />
        </div>
      </section>

      {/* ---------------------------------------------- Related */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-slate-900">Explore more products</h2>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {otherProducts.map((o) => (
              <Link key={o.slug} to={`/products/${o.slug}`} className="bg-white border border-slate-200 rounded-2xl p-5 hover:border-[#25D366] hover:-translate-y-1 transition-all" data-testid={`product-related-${o.slug}`}>
                <div className="text-sm font-semibold text-slate-900">{o.title}</div>
                <div className="mt-1 text-xs text-[#25D366] font-medium">Learn more →</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <FinalCta title="Put a QR Kit on your counter this week" sub="Book a free demo and we'll help you connect WhatsApp and Google Business and print your first posters." ctaSource={SLUG} />
    </div>
  );
}

/* ----------------------------------------------------------------------------
 * Pieces
 * ------------------------------------------------------------------------- */
function SectionHeading({ eyebrow, title, sub }) {
  return (
    <div className="max-w-3xl">
      {eyebrow && <div className="text-xs uppercase tracking-widest text-[#25D366] font-semibold mb-3">{eyebrow}</div>}
      <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">{title}</h2>
      {sub && <p className="mt-4 text-lg text-slate-600">{sub}</p>}
    </div>
  );
}

function FlowCard({ step, icon: Icon, color, title, points }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 flex flex-col">
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-xl grid place-items-center text-white" style={{ background: color }}>
          <Icon className="w-5 h-5" />
        </span>
        <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Step {step}</span>
      </div>
      <h3 className="mt-4 font-display text-xl font-semibold text-slate-900">{title}</h3>
      <ul className="mt-3 space-y-2">
        {points.map((pt) => (
          <li key={pt} className="flex items-start gap-2 text-sm text-slate-700">
            <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color }} /> {pt}
          </li>
        ))}
      </ul>
    </div>
  );
}

function PosterBuilder({ salesDigits }) {
  const [kit, setKit] = useState("whatsapp");
  const [themeKey, setThemeKey] = useState("brand");
  const [businessName, setBusinessName] = useState("");
  const [phone, setPhone] = useState("");
  const [waHeadline, setWaHeadline] = useState("Chat with us on WhatsApp");
  const [waMessage, setWaMessage] = useState("Hi! I would like to know more.");
  const [rateHeadline, setRateHeadline] = useState("How did we do?");
  const [logoSrc, setLogoSrc] = useState(null);
  const qrCanvasRef = useRef(null);

  const theme = KIT_THEMES.find((t) => t.key === themeKey) || KIT_THEMES[0];
  const ownDigits = toWaDigits(phone);
  const phoneInvalid = phone.trim() !== "" && !ownDigits;
  const digits = ownDigits || salesDigits;
  const link = useMemo(() => waLink(digits, waMessage), [digits, waMessage]);

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file.");
    if (file.size > 3 * 1024 * 1024) return toast.error("Logo must be under 3 MB.");
    const reader = new FileReader();
    reader.onload = () => setLogoSrc(reader.result);
    reader.readAsDataURL(file);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("WhatsApp link copied");
    } catch {
      toast.error("Couldn't copy — please copy the link manually.");
    }
  };

  const downloadQr = () => {
    const canvas = qrCanvasRef.current;
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `whatsapp-qr-${digits}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <section className="py-20 md:py-24 bg-[#0F172A] relative overflow-hidden scroll-mt-20" id="try-it" data-testid="qrkit-builder">
      <div className="absolute inset-0 bg-dots opacity-20" />
      <div className="absolute -top-24 -left-24 w-96 h-96 bg-[#25D366]/20 blur-3xl rounded-full" aria-hidden />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-widest text-[#25D366] font-semibold mb-3">Try it now</div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">Design your QR poster in seconds</h2>
          <p className="mt-4 text-lg text-slate-300">
            Pick a kit and style, add your business details and watch the poster update live. This is exactly what you'll get
            in the GrabNChat app.
          </p>
        </div>

        <div className="mt-12 grid lg:grid-cols-12 gap-10 items-start">
          {/* Controls */}
          <div className="min-w-0 lg:col-span-7 bg-white rounded-3xl p-5 sm:p-6 md:p-8 space-y-6">
            <div>
              <Label className="text-slate-900 font-semibold">Kit</Label>
              <div className="mt-2 grid grid-cols-2 gap-2" role="radiogroup" aria-label="QR kit">
                {[["whatsapp", "WhatsApp Chat"], ["rating", "Rate Us"]].map(([k, label]) => (
                  <button key={k} type="button" role="radio" aria-checked={kit === k} onClick={() => setKit(k)}
                    data-testid={`qrkit-builder-kit-${k}`}
                    className={`flex items-center justify-center gap-2 h-11 rounded-xl border text-sm font-semibold transition ${
                      kit === k ? "border-[#25D366] bg-[#25D366]/10 text-slate-900" : "border-slate-200 text-slate-600 hover:border-slate-300"
                    }`}>
                    {k === "whatsapp" ? <WhatsAppGlyph className="w-4 h-4 text-[#25D366]" /> : <Star className="w-4 h-4 text-[#F59E0B]" fill="currentColor" />}
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-slate-900 font-semibold">Style</Label>
              <div className="mt-2 flex flex-wrap gap-2" role="radiogroup" aria-label="Poster style">
                {KIT_THEMES.map((t) => (
                  <button key={t.key} type="button" role="radio" aria-checked={themeKey === t.key} onClick={() => setThemeKey(t.key)}
                    data-testid={`qrkit-builder-style-${t.key}`}
                    className={`flex items-center gap-2 h-10 px-4 rounded-full border text-sm font-medium transition ${
                      themeKey === t.key ? "text-slate-900" : "border-slate-200 text-slate-600 hover:border-slate-300"
                    }`}
                    style={themeKey === t.key ? { borderColor: t.primary, background: `${t.primary}14` } : undefined}>
                    <span className="w-4 h-4 rounded-full" style={{ background: `linear-gradient(135deg, ${t.primary}, ${t.secondary})` }} />
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="qk-name" className="text-slate-900 font-semibold">Business name</Label>
                <Input id="qk-name" className="mt-2" maxLength={40} placeholder="e.g. Sharma Sweets" value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)} data-testid="qrkit-builder-name" />
              </div>
              <div>
                <Label className="text-slate-900 font-semibold">Logo (optional)</Label>
                <div className="mt-2 flex items-center gap-2">
                  <label className="flex-1 flex items-center justify-center gap-2 h-10 rounded-md border border-dashed border-slate-300 text-sm text-slate-600 cursor-pointer hover:border-slate-400">
                    <ImagePlus className="w-4 h-4" /> {logoSrc ? "Change logo" : "Upload logo"}
                    <input type="file" accept="image/*" className="sr-only" onChange={onLogo} data-testid="qrkit-builder-logo" />
                  </label>
                  {logoSrc && (
                    <button type="button" onClick={() => setLogoSrc(null)} className="h-10 w-10 grid place-items-center rounded-md border border-slate-200 text-slate-500 hover:text-slate-900" aria-label="Remove logo">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {kit === "whatsapp" ? (
              <>
                <div>
                  <Label htmlFor="qk-phone" className="text-slate-900 font-semibold">WhatsApp number</Label>
                  <Input id="qk-phone" className="mt-2" inputMode="tel" placeholder="e.g. 98765 43210 or +44 7700 900123" value={phone}
                    onChange={(e) => setPhone(e.target.value)} aria-invalid={phoneInvalid} data-testid="qrkit-builder-phone" />
                  <p className={`mt-1.5 text-xs ${phoneInvalid ? "text-red-600" : "text-slate-500"}`}>
                    {phoneInvalid
                      ? "Enter a valid number with country code (10-digit numbers are treated as India +91)."
                      : ownDigits
                        ? "The QR now opens a chat with your number."
                        : "Until you add your number, the sample QR opens a chat with the GrabNChat team."}
                  </p>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="qk-wa-head" className="text-slate-900 font-semibold">Headline</Label>
                    <Input id="qk-wa-head" className="mt-2" maxLength={40} value={waHeadline} onChange={(e) => setWaHeadline(e.target.value)} />
                  </div>
                  <div>
                    <Label htmlFor="qk-wa-msg" className="text-slate-900 font-semibold">Pre-filled message</Label>
                    <Input id="qk-wa-msg" className="mt-2" maxLength={120} value={waMessage} onChange={(e) => setWaMessage(e.target.value)} placeholder="Optional" />
                  </div>
                </div>
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 flex items-center gap-3">
                  <Link2 className="w-4 h-4 text-slate-500 shrink-0" />
                  <code className="flex-1 min-w-0 truncate text-xs text-slate-700" title={link}>{link}</code>
                  <button type="button" onClick={copyLink} className="text-slate-500 hover:text-slate-900" aria-label="Copy WhatsApp link" data-testid="qrkit-builder-copy">
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button type="button" onClick={downloadQr} className="w-full sm:w-auto bg-[#25D366] hover:bg-[#1DA851] text-white rounded-full h-11 font-semibold" data-testid="qrkit-builder-download">
                    <Download className="w-4 h-4 mr-2" /> Download QR code (PNG)
                  </Button>
                  <a href={link} target="_blank" rel="noopener noreferrer">
                    <Button type="button" variant="outline" className="w-full rounded-full h-11 font-semibold">
                      <Eye className="w-4 h-4 mr-2" /> Test the link
                    </Button>
                  </a>
                </div>
                {/* Hi-res canvas used only for the PNG download. */}
                <div className="hidden" aria-hidden>
                  <QRCodeCanvas ref={qrCanvasRef} value={link} size={1024} level="H" marginSize={4} />
                </div>
              </>
            ) : (
              <>
                <div>
                  <Label htmlFor="qk-rate-head" className="text-slate-900 font-semibold">Headline</Label>
                  <Input id="qk-rate-head" className="mt-2" maxLength={32} value={rateHeadline} onChange={(e) => setRateHeadline(e.target.value)} />
                </div>
                <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                  <Sparkles className="w-4 h-4 shrink-0 mt-0.5" />
                  <p>
                    This preview uses a <strong>sample QR</strong>. Your real short rating link is created automatically in the
                    GrabNChat app once your Google Business Profile is connected.
                  </p>
                </div>
              </>
            )}

            <div className="pt-2 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <p className="text-sm text-slate-600 flex items-center gap-2">
                <BadgeCheck className="w-4 h-4 text-[#25D366] shrink-0" /> Get the full print-ready poster in the GrabNChat app.
              </p>
              <DemoFormDialog source={`${SLUG}-builder`} defaultMessage="I tried the QR Kit builder and want to set it up for my business." trigger={
                <Button variant="outline" className="rounded-full h-10 font-semibold" data-testid="qrkit-builder-demo-btn">
                  Get my QR Kit <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              } />
            </div>
          </div>

          {/* Live preview */}
          <div className="min-w-0 lg:col-span-5 lg:sticky lg:top-24 flex flex-col items-center">
            <ScaledPoster
              kit={kit}
              theme={theme}
              businessName={businessName}
              logoSrc={logoSrc}
              headline={kit === "whatsapp" ? waHeadline : rateHeadline}
              qrValue={kit === "whatsapp" ? link : SAMPLE_RATING_URL}
              pillText={kit === "whatsapp" ? prettyPhone(digits) : SAMPLE_RATING_PILL}
            />
            <p className="mt-4 text-xs text-slate-400">Live preview · A-series (1 : 1.414) poster</p>
          </div>
        </div>
      </div>
    </section>
  );
}
